import { FC } from "react";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import {
  ChartConfig,
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
} from "@/components/ui/chart";
import { RadialBarChart, RadialBar, PolarAngleAxis } from "recharts";

interface CardPlanRadialChartProps {
  title?: string;
  actualRevenue: number;
  plannedRevenue: number;
  actualProfit: number;
  plannedProfit: number;
  actualOrders: number;
  plannedOrders: number;
}

export const CardPlanRadialChart: FC<CardPlanRadialChartProps> = ({
  title = "Выполнение плана",
  actualRevenue = 0,
  plannedRevenue = 0,
  actualProfit = 0,
  plannedProfit = 0,
  actualOrders = 0,
  plannedOrders = 0,
}) => {
  const chartConfig = {
    revenue: {
      label: "Выручка",
      color: "hsl(var(--chart-3))",
    },
    profit: {
      label: "Чистая Прибыль",
      color: "hsl(var(--chart-1))",
    },
    orders: {
      label: "Заказы",
      color: "hsl(var(--chart-2))",
    },
  } satisfies ChartConfig;

  const chartData = [
    {
      key: "revenue",
      label: "Выручка",
      planned: plannedRevenue,
      actual: Math.floor(actualRevenue),
      value: Math.floor(plannedRevenue ? Math.min((actualRevenue / plannedRevenue) * 100, 100) : 0),
      fill: "var(--color-revenue)",
    },
    {
      key: "profit",
      label: "Чистая Прибыль",
      planned: plannedProfit,
      actual: actualProfit,
      value: Math.floor(plannedProfit ? Math.min((actualProfit / plannedProfit) * 100, 100) : 0),
      fill: "var(--color-profit)",
    },
    {
      key: "orders",
      label: "Заказы",
      planned: plannedOrders,
      actual: actualOrders,
      value: Math.floor(plannedOrders ? Math.min((actualOrders / plannedOrders) * 100, 100) : 0),
      fill: "var(--color-orders)",
    },
  ];

  return (
    <Card className="p-4 flex flex-col justify-between h-fit">
      <CardHeader className="pb-2">
        <CardTitle>{title}</CardTitle>
      </CardHeader>

      <CardContent>
        <div className="flex items-center justify-between">
          <div className="flex flex-col gap-3">
            {chartData.map((item) => (
              <div key={item.key}>
                <div className="flex items-center gap-2">
                  <div
                    className={`w-2 h-2 rounded-full`}
                    style={{
                      backgroundColor: chartConfig[item.key as keyof typeof chartConfig].color,
                    }}
                  />
                  <div className="text-sm text-muted-foreground">{item.label}</div>
                </div>
                <div className="text-lg font-semibold mt-1">
                  {Number(item.actual).toLocaleString()} / {Number(item.planned).toLocaleString()}
                  {item.key === "orders" ? " шт." : " ₽"}
                </div>
              </div>
            ))}
          </div>

          <ChartContainer config={chartConfig} className="min-h-[250px] h-48 aspect-square">
            <RadialBarChart
              data={chartData}
              innerRadius="30%"
              outerRadius="100%"
              startAngle={90}
              endAngle={-270}
              barSize={20}
            >
              <PolarAngleAxis type="number" domain={[0, 100]} angleAxisId={0} tick={false} />
              <RadialBar background dataKey="value" cornerRadius={10} label={false} />
              <ChartTooltip
                cursor={false}
                content={<ChartTooltipContent hideLabel nameKey="key" className="w-64" />}
              />
            </RadialBarChart>
          </ChartContainer>
        </div>
      </CardContent>
    </Card>
  );
};
